import { useTranslation } from "@sagot/i18n";
import { Icon, Input, cn, cssVars } from "@sagot/ui";
import { useId, useState } from "react";
import { BRAND_PRESETS, brandContrast } from "./widget-model";

interface BrandColorPickerProps {
  value: string;
  onChange: (hex: string) => void;
  /** Server-side warning for the saved colour (`WidgetOut.contrast.warning`). */
  serverWarning?: string | null;
  disabled?: boolean;
}

const HEX_INPUT = /^#[\da-f]{6}$/i;

/** Native colour input + hex field + presets, with the auto on-brand text and an AA warning. */
export function BrandColorPicker({ value, onChange, serverWarning, disabled }: BrandColorPickerProps) {
  const { t } = useTranslation();
  const id = useId();
  // Local text while typing a partial hex; follows `value` whenever it changes from outside.
  const [local, setLocal] = useState({ text: value, source: value });
  const text = local.source === value ? local.text : value;
  const contrast = brandContrast(value);
  const warning = contrast.tooLow ? t("deployKnowledge.widgetStudio.appearance.tooLow") : contrast.meetsAa ? null : t("deployKnowledge.widgetStudio.appearance.aaWarning");
  const commitText = (next: string) => {
    const hex = (next.startsWith("#") ? next : `#${next}`).toLowerCase();
    const valid = HEX_INPUT.test(hex);
    setLocal({ text: next, source: valid ? hex : value });
    if (valid) onChange(hex);
  };
  return (
    <div>
      <span className="label" id={`${id}-label`}>
        {t("deployKnowledge.widgetStudio.appearance.brandColor")}
      </span>
      <div className="flex items-center gap-3">
        <input
          type="color"
          value={value}
          disabled={disabled}
          onChange={(e) => { onChange(e.target.value.toLowerCase()); }}
          className="h-10 w-12 shrink-0 cursor-pointer rounded-md border border-line-strong bg-surface p-1 disabled:cursor-not-allowed"
          aria-label={t("deployKnowledge.widgetStudio.appearance.pickColor")}
        />
        <div className="min-w-0 flex-1">
          <Input
            className="h-8 w-28 font-mono text-sm font-semibold"
            value={text}
            disabled={disabled}
            maxLength={7}
            invalid={!HEX_INPUT.test(text)}
            aria-label={t("deployKnowledge.widgetStudio.appearance.brandHex")}
            onChange={(e) => { commitText(e.target.value.trim()); }}
          />
          <div className="mt-1 text-xs text-fg-subtle" data-testid="on-brand">
            {t("deployKnowledge.widgetStudio.appearance.onBrand")}{" "}
            <span className="font-semibold text-fg">
              {contrast.tone === "dark" ? t("deployKnowledge.widgetStudio.appearance.onBrandDark") : t("deployKnowledge.widgetStudio.appearance.onBrandLight")}
            </span>{" "}
            {t("deployKnowledge.widgetStudio.appearance.onBrandAuto")} · {t("deployKnowledge.widgetStudio.appearance.ratio", { ratio: contrast.ratio.toFixed(2) })}
          </div>
        </div>
      </div>
      <div className="mt-3 flex flex-wrap gap-2" role="group" aria-label={t("deployKnowledge.widgetStudio.appearance.presets")}>
        {BRAND_PRESETS.map((preset) => {
          const name = t(`deployKnowledge.widgetStudio.appearance.presetNames.${preset.key}`);
          return (
            <button
              key={preset.key}
              type="button"
              disabled={disabled}
              aria-pressed={value === preset.hex}
              aria-label={name}
              title={name}
              onClick={() => { onChange(preset.hex); }}
              style={cssVars({ "--wg-primary": preset.hex })}
              className={cn(
                "size-8 rounded-md border border-line bg-[var(--wg-primary)] ring-offset-2 ring-offset-surface aria-pressed:ring-2 aria-pressed:ring-fg disabled:cursor-not-allowed",
              )}
            />
          );
        })}
      </div>
      {warning ?? serverWarning ? (
        <p className="mt-2 flex items-start gap-1.5 text-xs font-semibold text-warning" role="status" data-testid="contrast-warning">
          <Icon name="alert-triangle" className="mt-0.5 size-3.5 shrink-0" />
          <span>{warning ?? serverWarning}</span>
        </p>
      ) : null}
    </div>
  );
}
